<template>
  <div class="config-container">
    <div class="page-header">
      <el-button @click="goBack">返回应用列表</el-button>
      <h2 class="page-title">
        {{ appInfo?.name || '应用' }} 配置
        <span v-if="packageName" class="package-name">（{{ packageName }}）</span>
      </h2>
    </div>
    <div class="table-section">
      <div class="toolbar">
        <span class="toolbar-title">配置列表</span>
        <el-button type="primary" @click="openDialog()">新增配置</el-button>
      </div>
      <el-table :data="configList" border stripe style="width: 100%;">
        <el-table-column prop="package_name" label="package_name" min-width="180" />
        <el-table-column prop="app_version" label="最小版本" width="110" />
        <el-table-column prop="ads_sky_appid" label="ads_sky_appid" min-width="140" />
        <el-table-column prop="ads_sky_siteid" label="ads_sky_siteid" min-width="120" />
        <el-table-column prop="ads_open" label="ads_open" width="100">
          <template #default="scope">
            <el-tag :type="scope.row.ads_open ? 'success' : 'info'">
              {{ scope.row.ads_open ? '是' : '否' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="ads_delay" label="ads_delay" width="100" />
        <el-table-column prop="ads_day" label="ads_day" width="100" />
        <el-table-column prop="ads_level" label="ads_level" width="100" />
        <el-table-column prop="url" label="url" min-width="180">
          <template #default="scope">
            <span class="ellipsis">{{ scope.row.url }}</span>
          </template>
        </el-table-column>
        <el-table-column label="扩展字段" width="100">
          <template #default="scope">
            {{ Object.keys(scope.row.extraFields || {}).length }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right">
          <template #default="scope">
            <el-button size="small" @click="openDialog(scope.row)">编辑</el-button>
            <el-button size="small" type="danger" @click="handleDelete(scope.row.id)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-dialog
      v-model="dialogVisible"
      :title="form.id ? '编辑配置' : '新增配置'"
      width="760px"
      :close-on-click-modal="false"
    >
      <el-form :model="form" label-width="130px">
        <el-form-item label="package_name" required>
          <el-input v-model="form.package_name" disabled />
        </el-form-item>
        <el-form-item label="app_version" required>
          <el-input v-model="form.app_version" placeholder="最小版本号，如 1.0.0" />
          <span class="field-hint">客户端版本 >= 该值时匹配</span>
        </el-form-item>
        <el-form-item label="ads_open">
          <el-switch v-model="form.ads_open" />
          <span class="field-hint">是否初始化 SkySDK</span>
        </el-form-item>
        <el-form-item label="ads_delay">
          <el-input-number v-model="form.ads_delay" :min="0" :step="1" />
          <span class="field-hint">延迟秒数启动</span>
        </el-form-item>
        <el-form-item label="ads_day">
          <el-input-number v-model="form.ads_day" :step="1" />
        </el-form-item>
        <el-form-item label="ads_level">
          <el-input-number v-model="form.ads_level" :step="1" />
        </el-form-item>
        <el-form-item label="ads_sky_appid" required>
          <el-input v-model="form.ads_sky_appid" placeholder="skymobi 的 appid" />
        </el-form-item>
        <el-form-item label="ads_sky_siteid">
          <el-input v-model="form.ads_sky_siteid" placeholder="skymobi 的 siteid" />
        </el-form-item>
        <el-form-item label="url">
          <el-input v-model="form.url" placeholder="H5 url" />
        </el-form-item>
        <el-form-item label="扩展字段">
          <div class="extra-fields">
            <div
              v-for="(item, idx) in form.extraFields"
              :key="idx"
              class="extra-field-row"
            >
              <el-input
                v-model="item.key"
                placeholder="key"
                style="width: 140px;"
                @blur="dedupeExtraFields"
              />
              <el-select v-model="item.type" style="width: 110px;" @change="onExtraTypeChange(item)">
                <el-option label="string" value="string" />
                <el-option label="int" value="int" />
                <el-option label="bool" value="bool" />
              </el-select>
              <el-switch v-if="item.type === 'bool'" v-model="item.value" />
              <el-input-number
                v-else-if="item.type === 'int'"
                v-model="item.value"
                :step="1"
              />
              <el-input
                v-else
                v-model="item.value"
                placeholder="value"
                style="flex: 1;"
              />
              <el-button type="danger" @click="removeExtraField(idx)">删除</el-button>
            </div>
            <el-button @click="addExtraField">+ 新增扩展字段</el-button>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveConfig">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { SkyMobiAppApi, type SkyMobiAppItem } from '../../api/skyMobiAppApi'
import {
  SkyMobiConfigApi,
  type ExtraFieldItem,
  type ExtraFieldType,
  type ExtraFieldsMap,
  type SkyMobiConfigItem,
  type SkyMobiConfigPayload,
} from '../../api/skyMobiConfigApi'

const route = useRoute()
const router = useRouter()
const packageName = computed(() => decodeURIComponent(String(route.params.packageName || '')))
const appInfo = ref<SkyMobiAppItem | null>(null)
const configList = ref<SkyMobiConfigItem[]>([])
const dialogVisible = ref(false)
const saving = ref(false)

const emptyForm = () => ({
  id: '',
  package_name: packageName.value,
  app_version: '1.0.0',
  ads_open: false,
  ads_delay: 0,
  ads_day: 0,
  ads_level: 0,
  ads_sky_appid: '',
  ads_sky_siteid: '',
  url: '',
  extraFields: [] as ExtraFieldItem[],
})

const form = ref(emptyForm())

function goBack() {
  router.push({ name: 'SkyMobiConfig' })
}

function defaultValueForType(type: ExtraFieldType): string | number | boolean {
  if (type === 'bool') return false
  if (type === 'int') return 0
  return ''
}

function inferType(value: unknown): ExtraFieldType {
  if (typeof value === 'boolean') return 'bool'
  if (typeof value === 'number') return 'int'
  return 'string'
}

function mapToExtraItems(extraFields?: ExtraFieldsMap | ExtraFieldItem[] | null): ExtraFieldItem[] {
  if (!extraFields) return []
  if (Array.isArray(extraFields)) {
    return extraFields.map((f) => ({
      key: f.key,
      type: f.type || inferType(f.value),
      value: f.value,
    }))
  }
  return Object.entries(extraFields).map(([key, value]) => ({
    key,
    type: inferType(value),
    value,
  }))
}

function coerceValue(type: ExtraFieldType, value: string | number | boolean): string | number | boolean {
  if (type === 'bool') return !!value
  if (type === 'int') return Number(value) || 0
  return value == null ? '' : String(value)
}

function dedupeExtraFields() {
  const seen = new Set<string>()
  const next: ExtraFieldItem[] = []
  let removed = 0
  for (const item of form.value.extraFields) {
    const key = item.key.trim()
    if (key && seen.has(key)) {
      removed += 1
      continue
    }
    if (key) seen.add(key)
    next.push(item)
  }
  if (removed > 0) {
    form.value.extraFields = next
    ElMessage.warning(`已去除 ${removed} 个重复的扩展字段 key`)
  }
}

function buildExtraFieldsMap(items: ExtraFieldItem[]): ExtraFieldsMap | null {
  const result: ExtraFieldsMap = {}
  const seen = new Set<string>()
  for (let i = 0; i < items.length; i += 1) {
    const key = items[i].key.trim()
    if (!key) {
      ElMessage.warning(`扩展字段第 ${i + 1} 行请填写 key`)
      return null
    }
    if (seen.has(key)) {
      ElMessage.warning(`扩展字段 key「${key}」重复，请去重后再保存`)
      return null
    }
    seen.add(key)
    result[key] = coerceValue(items[i].type, items[i].value)
  }
  return result
}

function addExtraField() {
  form.value.extraFields.push({ key: '', type: 'string', value: '' })
}

function removeExtraField(idx: number) {
  form.value.extraFields.splice(idx, 1)
}

function onExtraTypeChange(item: ExtraFieldItem) {
  item.value = defaultValueForType(item.type)
}

async function loadApp() {
  const list = await SkyMobiAppApi.getList()
  appInfo.value = list.find((a) => a.package_name === packageName.value) || null
  if (!appInfo.value) {
    ElMessage.error('应用不存在')
    goBack()
  }
}

async function loadList() {
  if (!packageName.value) return
  configList.value = await SkyMobiConfigApi.getList({
    package_name: packageName.value,
  })
}

function openDialog(row?: SkyMobiConfigItem) {
  if (row) {
    form.value = {
      id: row.id,
      package_name: packageName.value,
      app_version: row.app_version || '1.0.0',
      ads_open: !!row.ads_open,
      ads_delay: Number(row.ads_delay) || 0,
      ads_day: Number(row.ads_day) || 0,
      ads_level: Number(row.ads_level) || 0,
      ads_sky_appid: row.ads_sky_appid || '',
      ads_sky_siteid: row.ads_sky_siteid || '',
      url: row.url || '',
      extraFields: mapToExtraItems(row.extraFields),
    }
  } else {
    form.value = emptyForm()
  }
  dialogVisible.value = true
}

async function saveConfig() {
  if (!packageName.value) return
  const appVersion = form.value.app_version.trim()
  const adsSkyAppid = form.value.ads_sky_appid.trim()
  if (!appVersion) {
    ElMessage.warning('请填写 app_version')
    return
  }
  if (!adsSkyAppid) {
    ElMessage.warning('请填写 ads_sky_appid')
    return
  }
  dedupeExtraFields()
  const extraFields = buildExtraFieldsMap(form.value.extraFields)
  if (!extraFields) return

  const payload: SkyMobiConfigPayload = {
    package_name: packageName.value,
    app_version: appVersion,
    ads_open: form.value.ads_open,
    ads_delay: Number(form.value.ads_delay) || 0,
    ads_day: Number(form.value.ads_day) || 0,
    ads_level: Number(form.value.ads_level) || 0,
    ads_sky_appid: adsSkyAppid,
    ads_sky_siteid: form.value.ads_sky_siteid.trim(),
    url: form.value.url.trim(),
    extraFields,
  }
  saving.value = true
  try {
    if (form.value.id) {
      await SkyMobiConfigApi.update(form.value.id, payload)
      ElMessage.success('已更新')
    } else {
      await SkyMobiConfigApi.create(payload)
      ElMessage.success('已创建')
    }
    dialogVisible.value = false
    await loadList()
  } catch (error: any) {
    ElMessage.error(error?.response?.data?.message || error?.message || '保存失败')
  } finally {
    saving.value = false
  }
}

async function handleDelete(id: string) {
  try {
    await ElMessageBox.confirm('确定删除该配置？', '提示', { type: 'warning' })
    await SkyMobiConfigApi.delete(id)
    ElMessage.success('已删除')
    await loadList()
  } catch (error: any) {
    if (error !== 'cancel') {
      ElMessage.error(error?.response?.data?.message || error?.message || '删除失败')
    }
  }
}

async function bootstrap() {
  await loadApp()
  await loadList()
}

watch(packageName, () => {
  bootstrap().catch((error: any) => {
    ElMessage.error(error?.response?.data?.message || error?.message || '加载失败')
  })
})

onMounted(() => {
  bootstrap().catch((error: any) => {
    ElMessage.error(error?.response?.data?.message || error?.message || '加载失败')
  })
})
</script>

<style scoped>
.config-container {
  padding: 8px;
}
.page-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}
.page-title {
  margin: 0;
  font-size: 20px;
}
.package-name {
  font-size: 14px;
  color: var(--el-text-color-secondary);
  font-weight: 400;
}
.table-section {
  background: var(--el-bg-color);
  padding: 16px;
  border-radius: 8px;
}
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  gap: 12px;
}
.toolbar-title {
  font-weight: 600;
}
.ellipsis {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.field-hint {
  margin-left: 12px;
  color: var(--el-text-color-secondary);
  font-size: 13px;
}
.extra-fields {
  width: 100%;
}
.extra-field-row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}
</style>
